import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  ElementRef,
  Renderer2,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { TargetsService } from '@app/shared/services/targets.service';
import { PopoverComponent } from '@app/shared/components/popover/popover.component';
import { PopoverService } from '@app/shared/services/popover.service';
import { A11yService } from '@app/shared/services/a11y.service';
import { isKey, Key } from '@app/shared/key';
import {
  JobRole,
  OnboardingService,
} from '@app/onboarding/services/onboarding.service';
import { OnboardingStateService } from '@app/onboarding/services/onboarding-state.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'dgx-role-search',
  templateUrl: './role-search.component.html',
  styleUrls: ['./role-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoleSearchComponent implements OnInit {
  @ViewChild('rolesSearchInput') public rolesSearchInputRef: ElementRef;
  @ViewChild('popoverComponent') public popoverComponent: PopoverComponent;
  public roleSearchTerm: string = '';
  public isPopoverOpen: boolean = false;
  public suggestions: JobRole[] = [];
  public i18n = this.translateService.instant([
    'Onboarding_SearchRoles_SearchForRole',
  ]);
  public loadingResults: boolean = false;
  private unlisteners: (() => void)[] = [];

  private suggest = new Subject<any>();

  constructor(
    private targetsService: TargetsService,
    private renderer: Renderer2,
    private popoverService: PopoverService,
    private a11yService: A11yService,
    private cdr: ChangeDetectorRef,
    private onboardingStateService: OnboardingStateService,
    private onboardingService: OnboardingService,
    private translateService: TranslateService
  ) {}

  public ngOnInit(): void {
    this.roleSearchTerm = null;

    this.suggest
      .pipe(
        debounceTime(300),
        distinctUntilChanged(), // don't retrigger unless term has changed
        switchMap((roleSearchTerm) => {
          if (roleSearchTerm.length > 1) {
            // term must have at least two characters
            this.loadingResults = true;
            this.cdr.detectChanges();

            return this.targetsService.getSuggestedTitles(
              'JobRole',
              this.roleSearchTerm,
              5
            );
          }
          this.isPopoverOpen = false;
          this.cdr.detectChanges();
          return [];
        })
      )
      .subscribe((s) => {
        this.suggestions = s;
        this.isPopoverOpen = this.suggestions?.length > 0;
        this.loadingResults = false;
        this.cdr.detectChanges();
      });
  }

  /**
   * Focus on tag search element
   */
  public focusRoleSearch(): void {
    this.rolesSearchInputRef?.nativeElement.focus();
  }

  /**
   * Get next suggestion from search value entered
   * @param value search term entered
   */
  public onInputChange(event: Event) {
    this.suggest.next((<HTMLInputElement>event.target).value);
  }

  /**
   * @param newRole Job role to be added to grid and selected
   */
  public addRole(newRole: JobRole): void {
    if (newRole) {
      let roleToAdd = newRole;
      this.roleSearchTerm = null;
      roleToAdd.selected = true;
      roleToAdd.resourceType = 'Target';
      this.onboardingService.addFeaturedRole(roleToAdd);
      this.onboardingStateService.currentRoleChange(roleToAdd);
    }
  }

  public onIsPopoverOpenChange(isOpen: boolean) {
    this.isPopoverOpen = isOpen;
    if (isOpen) {
      this.unlisteners.push(
        this.renderer.listen(
          this.rolesSearchInputRef.nativeElement,
          'keydown',
          (ev) => {
            // on user tabbing close popover and go to next tabbable element
            if (isKey(ev, Key.Tab)) {
              this.popoverComponent.preventRefocus = true;
              this.popoverService.close();
            }
            if (isKey(ev, Key.Down)) {
              ev.preventDefault();
              this.a11yService.focusNextFocusable(
                this.popoverComponent.popover.nativeElement
              );
            }
          }
        )
      );
    } else {
      this.removeListeners();
    }
  }

  private removeListeners() {
    for (const unlisten of this.unlisteners) {
      unlisten();
    }
    this.unlisteners = [];
  }
}
